import { runningClientSide } from '@benzinga/utils';
import { nativoAdWrapperClassnames } from './constants';
import { NavitoEvents, NativoAdPlacementType, PostRelease } from './entities';
import type { BaseAdSlotEvent } from '../shared';

// https://www.benzinga.com/?ntv_pl=1918736&ntv_tm=tout
// https://admin.nativo.com/publications/44106/placements

declare global {
  interface Window {
    ntv: {
      [key: string]: any;
      CONSTS: [key: string];
      CompanionAsset: () => void;
      DFP: (e) => void;
      Events: NavitoEvents;
      PostRelease: PostRelease;
      cmd: Array<() => void>;
      version: string;
      initialized: boolean;
    };
    PostRelease: PostRelease;
  }
}

interface PageTargeting {
  [key: string]: string | string[] | boolean;
}

export interface NativoSlotRenderEvent extends BaseAdSlotEvent {
  nativoAdId: string | null;
  placementType: NativoAdPlacementType | null;
}

class NativoAdManager {
  private loaded = false;
  private initialized = false;
  private disabled = false;
  private targeting: PageTargeting = {};

  public isLoaded(): boolean {
    return this.loaded;
  }

  public isDisabled(): boolean {
    return this.disabled;
  }

  public init(shouldExecuteStartOnLoad = false): void {
    try {
      if (!runningClientSide()) return;
      if (this.disabled) return;

      this.initialized = true;
      this.log('Nativo ad script initialized');

      this.injectNativoScript({
        onLoad: () => {
          this.log('Nativo ad script injected successfully');
          shouldExecuteStartOnLoad && this.executeStart();
          this.loaded = true;
        },
      });
    } catch (error) {
      this.log('Error initializing Nativo ad script: ', error);
    }
  }

  public start(): void {
    if (!runningClientSide() || this.disabled || !this.initialized) return;
    this.executeStart();
  }

  public disable(): void {
    this.disabled = true;
  }

  public enable(): void {
    this.disabled = false;
  }

  public setTargeting(newTargeting: PageTargeting): PageTargeting {
    this.targeting = { ...this.targeting, ...(newTargeting || {}) };

    if (runningClientSide()) {
      window['ntvConfig'] = window['ntvConfig'] || {};
      window['ntvConfig'].keyValues = { ...(window['ntvConfig'].keyValues || {}), ...(this.targeting || {}) };
    }

    return this.targeting;
  }

  public getTargeting(): PageTargeting {
    return this.targeting;
  }

  public refresh(): void {
    this.loaded = false;
    this.start();
    this.log('Refreshed Nativo ads');
  }

  public observeAdLoad(
    placeholderSelector: string,
    callback: (event: NativoSlotRenderEvent) => void,
    options: { timeout?: number } = {},
  ): () => void {
    if (!runningClientSide()) {
      return function noopCleanup() {
        /* no-op for SSR */
      };
    }

    const { timeout = 30000 } = options;
    let timeoutId: NodeJS.Timeout | null = null;
    let observer: MutationObserver | null = null;
    let idleCallbackId: number | null = null;
    let resolved = false;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (observer) observer.disconnect();
      if (idleCallbackId && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
    };

    const tryGetAdInfo = (): boolean => {
      if (resolved) return true;

      const placeholder = document.querySelector(`.${placeholderSelector}`);
      if (!placeholder) return false;

      const nativoContent = placeholder.querySelector('[data-ntv-id]') as HTMLElement | null;
      if (!nativoContent) return false;

      const nativoAdId = nativoContent.getAttribute('data-ntv-id');
      const anchor = nativoContent.querySelector(
        'a.post-card-article-link[href*="ntv_r="], a.ntv-headline-anchor[href*="ntv_r="]',
      ) as HTMLAnchorElement | null;

      if (anchor) {
        resolved = true;
        cleanup();

        const rawUrl = anchor.href;
        const destinationUrl = this.extractNativoDestinationUrl(rawUrl);
        const placementType = this.getPlacementTypeFromSelector(placeholderSelector);

        const event: NativoSlotRenderEvent = {
          destinationUrl,
          isEmpty: false,
          nativoAdId,
          placeholderId: placeholderSelector,
          placementType,
          rawDestinationUrl: rawUrl,
        };

        this.log('[NativoAdManager] Ad loaded:', event);
        callback(event);
        return true;
      }
      return false;
    };

    const placeholder = document.querySelector(`.${placeholderSelector}`);
    if (placeholder) {
      observer = new MutationObserver(() => tryGetAdInfo());
      observer.observe(placeholder, { attributes: true, childList: true, subtree: true });
    }

    if (!tryGetAdInfo() && 'requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(() => tryGetAdInfo(), { timeout: 2000 });
    }

    timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        callback({
          destinationUrl: null,
          isEmpty: true,
          nativoAdId: null,
          placeholderId: placeholderSelector,
          placementType: this.getPlacementTypeFromSelector(placeholderSelector),
          rawDestinationUrl: null,
        });
      }
    }, timeout);

    return cleanup;
  }

  public extractNativoDestinationUrl(rawUrl: string): string {
    try {
      const allNtvRMatches = rawUrl.match(/ntv_r=([^&]+)/g);
      if (allNtvRMatches && allNtvRMatches.length > 0) {
        const lastMatch = allNtvRMatches[allNtvRMatches.length - 1];
        const encodedUrl = lastMatch.replace('ntv_r=', '');
        const decoded = decodeURIComponent(encodedUrl);

        if (decoded.startsWith('http')) {
          const cleanUrl = decoded.split('?')[0];
          return cleanUrl;
        }
      }
    } catch (error) {
      this.log('[NativoAdManager] Error extracting destination URL:', error);
    }
    return rawUrl;
  }

  private getPlacementTypeFromSelector(selector: string): NativoAdPlacementType | null {
    const entries = Object.entries(nativoAdWrapperClassnames) as [NativoAdPlacementType, string][];
    for (const [placementType, className] of entries) {
      if (selector === className || selector.includes(className)) {
        return placementType;
      }
    }
    return null;
  }

  private executeStart(): void {
    // if (!runningClientSide() || this.disabled || this.loaded) return;
    window.ntv = window.ntv || {};
    window.ntv.cmd = window.ntv.cmd || [];
    window.ntv.cmd.push(() => {
      try {
        window.PostRelease.Start();
        this.log('Nativo PostRelease started successfully');
      } catch (error) {
        this.log('Error starting Nativo PostRelease: ', error);
      }
    });
  }

  private injectNativoScript = ({
    onError,
    onLoad,
  }: {
    onError?: () => void;
    onLoad?: () => void;
  } = {}) => {
    const existingScript = document.getElementById('nativo-script');
    if (existingScript) {
      // Script already in the DOM (e.g. Soft nav / Strict Mode remount). Still invoke
      // onLoad so PostRelease.Start() runs for late-mounted placeholders — ntv.cmd
      // queues until Nativo is ready.
      typeof onLoad === 'function' && onLoad();
      return;
    }

    const scriptElement = document.createElement('script');
    scriptElement.id = 'nativo-script';
    scriptElement.src = 'https://s.ntv.io/serve/load.js';
    scriptElement.async = true;
    scriptElement.setAttribute('data-ntv-set-no-auto-start', '');
    scriptElement.type = 'text/javascript';

    if (typeof onLoad === 'function') {
      scriptElement.onload = onLoad;
    }
    scriptElement.onerror = (event: Event | string) => {
      if (typeof event === 'object' && event?.type === 'error') {
        console.error('Failed to inject Nativo script');
      }
      typeof onError === 'function' && onError();
    };

    document.head.appendChild(scriptElement);
  };

  private log(message: string, ...optionalParams: any[]): void {
    if (!runningClientSide()) return;
    if (new URLSearchParams(window.location.search).get('debug_nativo_ad_manager') === 'true') {
      console.log(`[Nativo Ad Manager] ${message}`, ...optionalParams);
    }
  }
}

export const nativoAdManager = new NativoAdManager();
